import React, { useEffect, useReducer, useRef, useCallback } from "react";
import styled from "styled-components";
import {
  startOfWeek,
  format,
  addDays,
  startOfDay,
  isSameDay,
  isToday,
} from "date-fns";
import Axios from "axios";
import range from "lodash/range";
import CalendarItem from "./CalendarItem";
import ItemInfo from "./ItemInfo";
import clamp from "lodash/clamp";
import {
  RECEIVE_DATA,
  MODE_NONE,
  NO_HIT,
  HIT,
  MOUSE_MOVE,
  MOUSE_DOWN,
  MOUSE_UP,
  STATUS_UNSAVED,
  SAVING,
  SAVED,
  DELETING,
  STATUS_DELETED,
  DELETED,
  STATUS_DELETING,
  CHANGE_COLOR,
  CHANGE_DATE,
  MODE_EDIT,
  CHANGE_INFO,
  DELETE_ITEM,
  RESIZE_ROW,
  SET_RANGE,
  DESELECT,
} from "./actions";
import {
  resolveItems,
  getExtent,
  formatTime,
  MIN_BLOCK,
  useIsMobile,
} from "./utils";
import { mainReducer } from "./mainReducer";
import useCurrentMinute from "../../hooks/useCurrentMinute";
import useWindowSize from "../../hooks/useWindowSize";
import { useSubscription } from "../../auth/Subscription";
import { useSettings } from "../../Settings";
import { useUser } from "../../auth";
import { usePageVisibility } from "react-page-visibility";
import { CalendarState, RegionHit, ShownRange } from "./types";
import { TimeFormat } from "@aika/types";
import { BrushSelect } from "./BrushSelect";
import { DateSelect } from "./DateSelect";
import { Toolbox } from "./Toolbox";
import { useBrushes } from "hooks/BrushesProvider";
import { inRange } from "lodash";
import { mix } from "polished";
import { MobileCalendar } from "./MobileCalendar";

const Container = styled.div``;

const Content = styled.main`
  flex: 1;
  display: flex;
  max-width: 100%;
  overflow: auto;
  min-height: calc(100vh - 50px);

  & p,
  & div {
    user-select: none;
  }
`;

const DayContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  margin-right: 2px;
  flex: 1;
  position: relative;
`;

const TimeColumn = styled.div<{ width: number }>`
  width: ${(props) => props.width}px;
  flex: 0 0 ${(props) => props.width}px;
`;

const DayColumn = styled.div`
  position: relative;
  flex: 1;

  &:after {
    content: " ";
    position: absolute;
    width: 0;
    height: 100%;
    top: 0;
    right: 0;
    border-right: 2px solid ${(props) => props.theme.colors.grey0};
    margin-right: -2px;
  }

  &:last-of-type:after {
    border-right: 0;
  }
`;

const HourSlot = styled.div<{
  height: number;
  lineWidth: number | string;
  color?: string;
  active: boolean;
}>`
  height: ${(props) => props.height}px;
  position: relative;
  pointer-events: none;

  &:nth-child(odd) {
    background-color: ${(props) =>
      mix(0.5, props.theme.colors.grey0, props.theme.colors.background)};
  }

  background-color: ${(props) =>
    props.active
      ? mix(0.8, props.theme.colors.background, props.theme.colors.primary)
      : "inherit"} !important;

  &:after {
    content: " ";
    position: absolute;
    width: ${(props) => props.lineWidth};
    height: 0;
    top: 0;
    right: 0;
    border-top: 2px solid
      ${(props) => props.theme.colors[props.color || "grey0"]};
    margin-top: -2px;
  }
`;

const HourMarker = styled.div<{ active: boolean }>`
  text-align: right;
  padding-right: 12px;
  font-size: 12px;
  top: -9px;
  position: relative;
  pointer-events: none;
  color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.body};
  font-weight: ${(props) => (props.active ? 600 : 500)};
`;

const ColumnHeader = styled.div<{ height: number; isToday: boolean }>`
  height: ${(props) => props.height}px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  user-select: none;
  pointer-events: none;
  color: ${(props) =>
    props.isToday ? props.theme.colors.primary : props.theme.colors.body};
`;

const TimeNeedle = styled.div`
  z-index: 500;
  height: 3px;
  width: calc(100% - 2px);
  position: absolute;
  pointer-events: none;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.body};
  border-radius: 2px;
  left: 2px;
  transform: translateY(-2px);
  &:before {
    content: "";
    width: 12px;
    height: 12px;
    background-color: ${(props) => props.theme.colors.background};
    border: 1px solid ${(props) => props.theme.colors.body};
    display: block;
    position: absolute;
    border-radius: 50%;
    top: -5px;
    left: -8px;
  }
`;

const AlertBar = styled.div`
  background-color: ${(props) => props.theme.colors.danger};
  padding: 12px;
  color: white;
  text-align: center;
`;

const RowLines = ({
  height,
  shownRange,
}: {
  height: number;
  shownRange: ShownRange;
}) => {
  const currentMinute = useCurrentMinute();

  return (
    <>
      {range(shownRange[0], shownRange[1] + 1, MIN_BLOCK).map((x) => (
        <HourSlot
          key={x}
          height={height}
          lineWidth="100%"
          active={inRange(currentMinute, x, x + MIN_BLOCK)}
        />
      ))}
    </>
  );
};

const DaysWrapper = styled.div`
  //touch-action: none;
  display: flex;
  flex: 1;
`;

const getToday = () => {
  var now = new Date();
  var utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
  return utc;
};

const initialState: CalendarState = {
  items: [],
  itemCache: [],
  activeItem: null,
  mode: MODE_NONE,
  rowHeight: 24,
  dayCount: 0,
  jumpCount: 7,
  startDate: startOfWeek(startOfDay(getToday()), { weekStartsOn: 1 }),
  lastDown: Date.now(),
  cursor: "default",
  brush: "color-0",
  shownRange: [0, 1440],
  algo: "move",
  dragType: "HIT",
  offset: {
    x: 0,
    y: 0,
  },
};

const CalendarPage = () => {
  const [state, dispatch] = useReducer(mainReducer, initialState);
  const { brushes } = useBrushes();
  const {
    items,
    activeItem,
    rowHeight,
    cursor,
    dayCount,
    startDate,
    shownRange,
    algo,
    brush,
  } = state;

  const { user } = useUser();
  const [subscription] = useSubscription();
  const dayContainerRef = useRef<HTMLDivElement>(null!);
  const currentMinute = useCurrentMinute();
  const previousMinute = useRef(currentMinute);
  const windowSize = useWindowSize();
  const isMobile = /Mobi/.test(window.navigator.userAgent);
  const [settings] = useSettings();
  const lastChange = useRef(null);
  const isPageVisible = usePageVisibility();
  const activeBrush = brushes.find((x) => x.id === brush) ?? brushes[0];
  const disableSchedule = subscription.type === "expired" || dayCount === 0;

  const notificationsEnabled =
    "Notification" in window && settings.enableNotifications;

  useEffect(() => {
    if (notificationsEnabled) {
      Notification.requestPermission();
    }
  }, [notificationsEnabled]);

  useEffect(() => {
    const start = settings.startOfDay || 0;
    const end = settings.endOfDay || 1440;
    const rangeType = isMobile ? 1 : settings.dayRange || 0;
    const dayCounts = [7, 1, 7, 3, 5, 7];
    const jumpCounts = [7, 1, 7, 3, 7, 7];
    const startDates = [
      startOfWeek(startOfDay(new Date()), { weekStartsOn: 1 }),
      startOfDay(new Date()),
      addDays(startOfDay(new Date()), -1),
      addDays(startOfDay(new Date()), -1),
      startOfWeek(startOfDay(new Date()), { weekStartsOn: 1 }),
      startOfWeek(startOfDay(new Date()), { weekStartsOn: 0 }),
    ];

    dispatch({
      type: SET_RANGE,
      payload: [
        start,
        end,
        dayCounts[rangeType],
        startDates[rangeType],
        jumpCounts[rangeType],
      ],
    });
  }, [settings, isMobile]);

  useEffect(() => {
    dispatch({
      type: RESIZE_ROW,
      payload: windowSize.height,
    });
  }, [windowSize]);

  const pull = useCallback(() => {
    if (dayCount > 0) {
      Axios.get(
        `/api/calendar?start=${format(
          startDate,
          "yyyy-MM-dd"
        )}&count=${dayCount}`
      ).then(({ data }) =>
        dispatch({
          type: RECEIVE_DATA,
          payload: data,
        })
      );
    }
  }, [dayCount, startDate]);

  useEffect(() => {
    pull();
  }, [dayCount, startDate, pull]);

  useEffect(() => {
    const i = setInterval(() => {
      if (isPageVisible) {
        Axios.get(`/api/users/${user.id}/ping`).then(({ data }) => {
          if (lastChange.current == null) {
            lastChange.current = data;
          } else if (data !== lastChange.current) {
            lastChange.current = data;
            pull();
          }
        });
      }
    }, 2000);
    return () => clearInterval(i);
  }, [pull, user, isPageVisible]);

  useEffect(() => {
    if (previousMinute.current === currentMinute) {
      return;
    }

    previousMinute.current = currentMinute;

    const today = new Date();
    const currentItem = items.find(
      (x) => isSameDay(new Date(x.date), today) && x.start === currentMinute
    );

    if (notificationsEnabled && currentItem != null) {
      const name = currentItem.description || "a new task.";

      const n = new Notification("Aika - Switch Tasks", {
        body: `Time to start ${name}`,
      });
      n.onclick = () => {
        window.focus();
      };
    }
  }, [currentMinute, items, notificationsEnabled]);

  useEffect(() => {
    items
      .filter((x) => x.status === STATUS_UNSAVED)
      .forEach((item) => {
        dispatch({
          type: SAVING,
          payload: item,
        });
        const spec = {
          ...item,
          date: format(item.date, "yyyy-MM-dd"),
        };
        Axios.post("/api/blocks", spec).then((x) => {
          lastChange.current = x.headers["x-lastchange"];
          dispatch({
            type: SAVED,
            payload: x.data,
          });
        });
      });
  }, [items]);

  useEffect(() => {
    items
      .filter((x) => x.status === STATUS_DELETED)
      .forEach((item) => {
        dispatch({
          type: DELETING,
          payload: item,
        });
        Axios.delete(`/api/blocks/${item.id}`).then((x) => {
          lastChange.current = x.headers["x-lastchange"];
          dispatch({
            type: DELETED,
            payload: item,
          });
        });
      });
  }, [items]);

  useEffect(() => {
    const handler = () => {
      dispatch({
        type: MOUSE_UP,
        payload: { brush: activeBrush },
      });
    };

    document.addEventListener("mouseup", handler);
    document.addEventListener("touchend", handler);

    return () => {
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("touchend", handler);
    };
  }, [activeBrush]);

  const resolved = resolveItems(
    items,
    activeItem,
    null,
    null,
    null,
    true,
    algo,
    shownRange[1]
  );

  const days = range(0, dayCount)
    .map((x) => addDays(startDate, x))
    .map((date) => ({
      date,
      items: resolved.filter(
        (x) =>
          x.status !== STATUS_DELETED &&
          x.status !== STATUS_DELETING &&
          isSameDay(new Date(x.date), date)
      ),
    }));

  const getRegion = (x: number, y: number, dayIndex: number): RegionHit => {
    const day = days[dayIndex];

    const item = day.items.find((x) => {
      const ex = getExtent(x, rowHeight, shownRange);
      return ex[0] <= y && ex[1] > y;
    });

    const base = {
      date: day.date,
      minute: Math.floor(y / rowHeight) * MIN_BLOCK + shownRange[0],
      y: y,
      x: x,
    };

    if (item == null) {
      return {
        ...base,
        type: NO_HIT,
      };
    }

    const extent = getExtent(item, rowHeight, shownRange);

    return {
      ...base,
      items: [item],
      type: HIT,
      minute:
        Math.round((y - extent[0]) / rowHeight) * MIN_BLOCK + shownRange[0],
      offset: {
        y: y - extent[0],
      },
    };
  };

  const toLocalCoords = (ev: React.MouseEvent | React.TouchEvent) => {
    const src = "touches" in ev ? ev.touches[0] : ev;
    var rect = ev.currentTarget.getBoundingClientRect();
    var x = src.clientX - rect.left;
    var y = src.clientY - rect.top;
    y -= 60;
    const dayIndex = clamp(
      Math.floor(x / (rect.width / dayCount)),
      0,
      dayCount - 1
    );
    return { x, y, dayIndex };
  };

  const onDayMouseMove = (ev: React.MouseEvent | React.TouchEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const { x, y, dayIndex } = toLocalCoords(ev);
    const hit = getRegion(x, y, dayIndex);

    dispatch({
      type: MOUSE_MOVE,
      payload: hit,
      altKey: ev.altKey,
    });

    return false;
  };

  const onMouseDown = (ev: React.MouseEvent | React.TouchEvent): boolean => {
    if ("button" in ev && ev.button !== 0) {
      return;
    }
    ev.preventDefault();
    const { x, y, dayIndex } = toLocalCoords(ev);
    const hit = getRegion(x, y, dayIndex);

    if (y < 0) {
      return;
    }

    dispatch({
      type: MOUSE_DOWN,
      payload: {
        region: hit,
        brush: activeBrush,
      },
    });

    return false;
  };

  const activeBlock =
    state.mode === MODE_EDIT && items.find((x) => x.id === activeItem);

  const getActiveBlockPosition = () => {
    const dayIndex = days.findIndex(
      (x) => x.items.find((i) => i.id === activeItem) != null
    );

    const perDay =
      (dayContainerRef.current.clientWidth - timeColWidth) / dayCount;

    let left = 0;
    let fromLeft = true;

    if (dayCount === 1) {
      left = 60;
    } else if (dayIndex > dayCount / 2) {
      left = timeColWidth + perDay * dayIndex - 12 - 250;
    } else {
      left = timeColWidth + perDay * (dayIndex + 1) + 12;
      fromLeft = false;
    }

    let top =
      60 + (activeBlock.start - shownRange[0]) * (state.rowHeight / MIN_BLOCK);

    top = Math.min(top, dayContainerRef.current.clientHeight - 270);

    return { left, top, fromLeft };
  };

  const getNeedleProps = () => {
    return {
      top: (currentMinute - shownRange[0]) * (state.rowHeight / MIN_BLOCK) + 60,
      display:
        currentMinute > shownRange[0] && currentMinute < shownRange[1]
          ? "block"
          : "none",
    };
  };

  const timeColWidth = settings.timeFormat === TimeFormat.TwelveHour ? 70 : 40;

  return (
    <Container>
      {isMobile && (
        <AlertBar>The schedule is currently read-only on mobile.</AlertBar>
      )}
      <Content>
        <Toolbox>
          <DateSelect
            value={state.startDate}
            onChange={(val) => dispatch({ type: CHANGE_DATE, payload: val })}
          />
          <BrushSelect
            value={brush}
            onChange={(id) => {
              dispatch({ type: CHANGE_COLOR, payload: id });
            }}
          />
        </Toolbox>
        <DayContainer ref={dayContainerRef}>
          {activeBlock && (
            <ItemInfo
              key={activeBlock.id}
              item={activeBlock}
              onCancel={() => {
                dispatch({
                  type: DESELECT,
                });
              }}
              {...getActiveBlockPosition()}
              onChange={(data) =>
                dispatch({
                  type: CHANGE_INFO,
                  payload: data,
                })
              }
              onDelete={() => {
                dispatch({
                  type: DELETE_ITEM,
                  payload: activeBlock,
                });
              }}
            />
          )}
          <TimeColumn width={timeColWidth}>
            <ColumnHeader height={60} isToday={false} />
            {range(shownRange[0], shownRange[1] + 1, MIN_BLOCK).map((x) => (
              <HourSlot
                key={x}
                height={rowHeight}
                lineWidth="6px"
                active={false}
              >
                <HourMarker active={false}>
                  {formatTime(x, settings.timeFormat)}
                </HourMarker>
              </HourSlot>
            ))}
          </TimeColumn>
          <DaysWrapper
            style={{ cursor: cursor }}
            onMouseMove={!disableSchedule ? onDayMouseMove : undefined}
            onTouchMove={!disableSchedule ? onDayMouseMove : undefined}
            onTouchStart={!disableSchedule ? onMouseDown : undefined}
            onMouseDown={!disableSchedule ? onMouseDown : undefined}
            onDragStart={() => false}
            draggable="false"
          >
            {days.map(({ date, items }) => (
              <DayColumn key={date.toString()}>
                <ColumnHeader
                  height={60}
                  draggable="false"
                  isToday={isToday(date)}
                >
                  {format(date, "iiii")}
                  <br />
                  {format(date, "yyyy-MM-dd")}
                </ColumnHeader>
                <RowLines height={rowHeight} shownRange={shownRange} />
                {items.map((x) => (
                  <CalendarItem
                    key={x.id}
                    {...x}
                    rowHeight={rowHeight}
                    headerOffset={60}
                    active={activeItem === x.id}
                    colorIndex={x.color}
                    shownRange={shownRange}
                  />
                ))}
                {isToday(date) && <TimeNeedle style={getNeedleProps()} />}
              </DayColumn>
            ))}
          </DaysWrapper>
        </DayContainer>
      </Content>
    </Container>
  );
};

export const CalendarSelector = () => {
  const isMobile = useIsMobile();

  return isMobile ? <MobileCalendar /> : <CalendarPage />;
};

export default CalendarPage;
